// 화면 공용 포맷/파생 헬퍼 — 디자인(_ClaudeDesign) 표기 규칙에 맞춤
import type { CSSProperties } from 'react';
import type { Attendance, BoardEntry } from '@dxtech/shared';
import type { WorkStatus } from '../ui/status.js';

/**
 * 임직원 보드 카드 스타일(상태별):
 * - 출근전(absent)/퇴근(off): 회색 음영 배경
 * - 출근(working)/휴가(vacation): 흰색 배경
 * - 출근 후 부재중(away): 붉은색 보더
 */
export function boardCardStyle(s: WorkStatus): CSSProperties {
  const base: CSSProperties = { border: '1px solid #EDEFF2', borderRadius: 12, padding: 14, background: '#fff' };
  if (s === 'away') return { ...base, border: '1.5px solid #DC2626' };
  if (s === 'off' || s === 'absent') return { ...base, background: '#F3F4F6' };
  return base;
}

/** ISO datetime → KST 'HH:mm' (없으면 '—') */
export function kstHHmm(iso?: string | null): string {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('sv-SE', {
    timeZone: 'Asia/Seoul', hour: '2-digit', minute: '2-digit', hour12: false,
  }).format(new Date(iso));
}

/** 'YYYY-MM-DD' → 'M/D' */
export function mmdd(d?: string): string {
  if (!d) return '';
  const p = d.split('-');
  if (p.length < 3) return d;
  return `${Number(p[1])}/${Number(p[2])}`;
}

/** 'YYYY-MM-DD' → 'M/D (요일)' */
export function mmddW(d?: string): string {
  if (!d) return '';
  const dt = new Date(`${d}T00:00:00+09:00`);
  if (Number.isNaN(dt.getTime())) return mmdd(d);
  const wd = ['일', '월', '화', '수', '목', '금', '토'][new Date(dt).getDay()];
  return `${mmdd(d)} (${wd})`;
}

/** KST 기준 오늘 'YYYY-MM-DD' */
export function kstToday(): string {
  return new Intl.DateTimeFormat('sv-SE', { timeZone: 'Asia/Seoul' }).format(new Date());
}

/** 출퇴근 기록 → 근무시간 라벨. 퇴근 미입력은 '오늘'만 '근무 중', 지난 날짜는 '—'(퇴근 누락) */
export function workedLabel(a?: Attendance | null): string {
  if (!a?.checkInAt) return '—';
  if (!a.checkOutAt) return a.workDate === kstToday() ? '근무 중' : '—';
  const ms = new Date(a.checkOutAt).getTime() - new Date(a.checkInAt).getTime();
  if (!(ms > 0)) return '—';
  const min = Math.round(ms / 60000);
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m ? `${h}시간 ${m}분` : `${h}시간`;
}

/** 출퇴근 기록 → 근태 상태 */
export function attStatus(a?: Attendance | null): WorkStatus {
  if (!a?.checkInAt) return 'absent';
  return a.checkOutAt ? 'off' : 'working';
}

/** 보드 엔트리 → 근태 상태. 서버 계산 status 우선, 없으면 폴백 추론. */
export function boardStatus(e: BoardEntry): WorkStatus {
  if (e.status) return e.status;
  if (e.away) return 'away';
  const oof = e.schedule?.some((s) => s.showAs === 'oof' || (s.isAllDay && s.showAs === 'busy'));
  if (oof) return 'vacation';
  return 'working';
}

/** upn에서 표시 이름 추정(보드에 없을 때 폴백) */
export function nameFromUpn(upn: string): string {
  return upn.split('@')[0] ?? upn;
}
